import Header from "@/components/feature/Header";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/base/Breadcrumb";
import PageMeta from "@/components/base/PageMeta";

const siteUrl = import.meta.env.VITE_PUBLIC_SITE_URL || "https://hackerslodge.com";

const privacySchema = {
  "@context": "https://schema.org",
  "@type": "WebPage",
  url: `${siteUrl}/privacy`,
  name: "Privacy Policy — Hackers Lodge",
  description:
    "Hackers Lodge Privacy Policy. Learn how we collect, use, and protect your personal data when you use our cybersecurity talent marketplace and escrow services.",
  isPartOf: {
    "@type": "WebSite",
    name: "Hackers Lodge",
    url: siteUrl,
  },
};

const sections = [
  {
    id: "introduction",
    title: "1. Introduction",
    content: (
      <p className="text-sm text-gray-400 leading-relaxed">
        Hackers Lodge (&ldquo;we,&rdquo; &ldquo;us,&rdquo; or &ldquo;our&rdquo;) is committed to protecting your
        privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you
        use our website, platform, and services (collectively, the &ldquo;Services&rdquo;). By accessing or using
        Hackers Lodge, you agree to the terms described in this policy. If you do not agree, please discontinue use
        of our Services immediately.
      </p>
    ),
  },
  {
    id: "information-we-collect",
    title: "2. Information We Collect",
    content: (
      <div className="space-y-3">
        <div>
          <h4 className="text-sm font-semibold text-gray-300 mb-1">2.1 Account Information</h4>
          <p className="text-sm text-gray-400 leading-relaxed">
            When you register an account, we collect your username, email address, and an encrypted version of your
            password. You may optionally provide a display name, profile avatar, bio, and professional credentials
            (certifications, skills, years of experience).
          </p>
        </div>
        <div>
          <h4 className="text-sm font-semibold text-gray-300 mb-1">2.2 Project &amp; Transaction Data</h4>
          <p className="text-sm text-gray-400 leading-relaxed">
            When you post, bid on, or accept a project, we collect project descriptions, scope documents, budget
            information, messages exchanged with other users, escrow transaction records, and crypto wallet
            addresses used for payments.
          </p>
        </div>
        <div>
          <h4 className="text-sm font-semibold text-gray-300 mb-1">2.3 Communication Data</h4>
          <p className="text-sm text-gray-400 leading-relaxed">
            When you contact us via forms, email, or the platform messaging system, we collect your name, email
            address, subject line, message body, and any attachments you provide.
          </p>
        </div>
        <div>
          <h4 className="text-sm font-semibold text-gray-300 mb-1">2.4 Automatically Collected Data</h4>
          <p className="text-sm text-gray-400 leading-relaxed">
            We automatically collect certain information when you visit our site, including your IP address, browser
            type, operating system, referring URLs, pages viewed, timestamps, and device identifiers. This data is
            collected via server logs and analytics tools.
          </p>
        </div>
        <div>
          <h4 className="text-sm font-semibold text-gray-300 mb-1">2.5 Cookies &amp; Tracking Technologies</h4>
          <p className="text-sm text-gray-400 leading-relaxed">
            We use cookies and similar tracking technologies to maintain your session, remember preferences, analyze
            site usage, and improve our Services. You can control cookie settings through your browser; however,
            disabling cookies may limit functionality.
          </p>
        </div>
      </div>
    ),
  },
  {
    id: "how-we-use",
    title: "3. How We Use Your Information",
    content: (
      <div className="space-y-2">
        <ul className="list-disc list-inside text-sm text-gray-400 space-y-1.5 leading-relaxed">
          <li>To create, manage, and secure your account.</li>
          <li>To facilitate project matching, escrow transactions, and payments between clients and hackers.</li>
          <li>To verify hacker identities, credentials, and professional backgrounds.</li>
          <li>To communicate with you about your account, projects, transactions, and support inquiries.</li>
          <li>To send service-related notifications, updates, and administrative messages.</li>
          <li>To detect, prevent, and investigate fraud, abuse, and security incidents.</li>
          <li>To analyze usage patterns and improve the performance and user experience of our platform.</li>
          <li>To comply with applicable legal obligations and enforce our Terms of Service.</li>
        </ul>
      </div>
    ),
  },
  {
    id: "data-sharing",
    title: "4. How We Share Your Information",
    content: (
      <div className="space-y-3">
        <div>
          <h4 className="text-sm font-semibold text-gray-300 mb-1">4.1 With Other Users</h4>
          <p className="text-sm text-gray-400 leading-relaxed">
            When you engage in a project, certain profile information (username, display name, bio, skills,
            credentials) is shared with the other party to facilitate collaboration. Contact details are only shared
            upon mutual agreement.
          </p>
        </div>
        <div>
          <h4 className="text-sm font-semibold text-gray-300 mb-1">4.2 Service Providers</h4>
          <p className="text-sm text-gray-400 leading-relaxed">
            We may share information with trusted third-party service providers who assist us in operating the
            platform, including hosting providers, email delivery services, analytics providers, and payment
            processors. These providers are contractually bound to protect your data.
          </p>
        </div>
        <div>
          <h4 className="text-sm font-semibold text-gray-300 mb-1">4.3 Legal Compliance</h4>
          <p className="text-sm text-gray-400 leading-relaxed">
            We may disclose your information if required by law, court order, or governmental authority, or if we
            believe disclosure is necessary to protect our rights, your safety, or the safety of others, or to
            investigate fraud or security breaches.
          </p>
        </div>
        <div>
          <h4 className="text-sm font-semibold text-gray-300 mb-1">4.4 Business Transfers</h4>
          <p className="text-sm text-gray-400 leading-relaxed">
            In the event of a merger, acquisition, or sale of all or a portion of our assets, your information may be
            transferred as part of that transaction. We will notify you before your data becomes subject to a
            different privacy policy.
          </p>
        </div>
        <div>
          <h4 className="text-sm font-semibold text-gray-300 mb-1">4.5 We Do Not Sell Your Data</h4>
          <p className="text-sm text-gray-400 leading-relaxed">
            Hackers Lodge does not sell, rent, or trade your personal information to third parties for their
            marketing purposes. We are not a data broker and have no intention of becoming one.
          </p>
        </div>
      </div>
    ),
  },
  {
    id: "data-retention",
    title: "5. Data Retention",
    content: (
      <p className="text-sm text-gray-400 leading-relaxed">
        We retain your personal information for as long as your account is active or as needed to provide you with
        our Services. If you delete your account, we will remove or anonymize your personal data within 30 days,
        except where retention is required by law (e.g., transaction records for tax purposes, logs for security
        investigations). Anonymized or aggregated data may be retained indefinitely for analytical purposes.
      </p>
    ),
  },
  {
    id: "data-security",
    title: "6. Data Security",
    content: (
      <div className="space-y-3">
        <p className="text-sm text-gray-400 leading-relaxed">
          We implement industry-standard technical and organizational measures to protect your data, including:
        </p>
        <ul className="list-disc list-inside text-sm text-gray-400 space-y-1.5 leading-relaxed">
          <li>End-to-end encryption for sensitive communications (where technically feasible).</li>
          <li>Encryption of data at rest and in transit using TLS 1.3.</li>
          <li>Row-Level Security (RLS) policies on our database to enforce strict access controls.</li>
          <li>Regular security audits and vulnerability assessments.</li>
          <li>Multi-factor authentication (MFA) support for user accounts.</li>
          <li>Secure escrow infrastructure for crypto transactions.</li>
        </ul>
        <p className="text-sm text-gray-400 leading-relaxed">
          However, no method of electronic transmission or storage is 100% secure. While we strive to use
          commercially acceptable means to protect your data, we cannot guarantee absolute security. You are
          responsible for maintaining the confidentiality of your login credentials.
        </p>
      </div>
    ),
  },
  {
    id: "your-rights",
    title: "7. Your Rights &amp; Choices",
    content: (
      <div className="space-y-3">
        <div>
          <h4 className="text-sm font-semibold text-gray-300 mb-1">7.1 Access &amp; Correction</h4>
          <p className="text-sm text-gray-400 leading-relaxed">
            You can access and update most of your personal information through your account settings at any time.
            For data not accessible via the dashboard, you may request a copy by contacting us.
          </p>
        </div>
        <div>
          <h4 className="text-sm font-semibold text-gray-300 mb-1">7.2 Data Portability</h4>
          <p className="text-sm text-gray-400 leading-relaxed">
            Upon request, we will provide your personal data in a structured, commonly used, machine-readable
            format. This applies to data you have actively provided and data generated through your use of our
            Services.
          </p>
        </div>
        <div>
          <h4 className="text-sm font-semibold text-gray-300 mb-1">7.3 Deletion</h4>
          <p className="text-sm text-gray-400 leading-relaxed">
            You may request deletion of your account and associated personal data at any time. We will comply within
            30 days, subject to legal retention obligations. Note that project-related records where you participated
            may be retained in anonymized form.
          </p>
        </div>
        <div>
          <h4 className="text-sm font-semibold text-gray-300 mb-1">7.4 Marketing Communications</h4>
          <p className="text-sm text-gray-400 leading-relaxed">
            You may opt out of marketing and promotional emails by clicking the &ldquo;unsubscribe&rdquo; link in
            any such email or by adjusting your notification preferences in account settings. Transactional and
            service-related messages cannot be opted out of while your account is active.
          </p>
        </div>
        <div>
          <h4 className="text-sm font-semibold text-gray-300 mb-1">7.5 Cookie Preferences</h4>
          <p className="text-sm text-gray-400 leading-relaxed">
            Most web browsers accept cookies by default. You can modify your browser settings to refuse cookies or
            alert you when cookies are being sent. Please note that some features may not function properly without
            cookies enabled.
          </p>
        </div>
      </div>
    ),
  },
  {
    id: "third-party",
    title: "8. Third-Party Services &amp; Links",
    content: (
      <p className="text-sm text-gray-400 leading-relaxed">
        Our platform may contain links to third-party websites, tools, or services that are not owned or controlled
        by Hackers Lodge. We are not responsible for the privacy practices of these third parties. We encourage you
        to review the privacy policies of any third-party service you interact with, including blockchain networks
        used for crypto payments, external analytics tools, and social media platforms linked from our website.
      </p>
    ),
  },
  {
    id: "children",
    title: "9. Children&rsquo;s Privacy",
    content: (
      <p className="text-sm text-gray-400 leading-relaxed">
        Our Services are not intended for individuals under the age of 18. We do not knowingly collect personal
        information from children. If we become aware that a child under 18 has provided us with personal data, we
        will delete such information promptly. If you believe a child has submitted personal data to us, please
        contact us immediately.
      </p>
    ),
  },
  {
    id: "international",
    title: "10. International Data Transfers",
    content: (
      <p className="text-sm text-gray-400 leading-relaxed">
        Hackers Lodge operates globally, and your information may be transferred to and processed in countries other
        than your country of residence. These countries may have data protection laws that differ from those in your
        jurisdiction. By using our Services, you consent to such transfers. We take appropriate safeguards to ensure
        your data remains protected in accordance with this Privacy Policy regardless of where it is processed.
      </p>
    ),
  },
  {
    id: "changes",
    title: "11. Changes to This Privacy Policy",
    content: (
      <p className="text-sm text-gray-400 leading-relaxed">
        We reserve the right to update this Privacy Policy at any time. When we make material changes, we will
        notify you via email (to the address associated with your account) and/or by posting a prominent notice on
        our website at least 14 days before the changes take effect. Your continued use of the Services after the
        effective date constitutes acceptance of the revised policy. We encourage you to review this page
        periodically.
      </p>
    ),
  },
  {
    id: "contact",
    title: "12. Contact Us",
    content: (
      <div className="space-y-3">
        <p className="text-sm text-gray-400 leading-relaxed">
          If you have questions, concerns, or requests regarding this Privacy Policy or our data practices, please
          contact us:
        </p>
        <div className="bg-[#161b22] rounded border border-gray-700 p-4 space-y-2">
          <p className="text-sm text-gray-300">
            <strong className="text-gray-200">Support:</strong>{" "}
            <a href="mailto:support@hackerslodge.com" className="text-[#5bc0de] hover:underline">
              support@hackerslodge.com
            </a>
          </p>
          <p className="text-sm text-gray-300">
            <strong className="text-gray-200">Contact Form:</strong>{" "}
            <a href="/contact" className="text-[#5bc0de] hover:underline">
              hackerslodge.com/contact
            </a>
          </p>
        </div>
        <p className="text-xs text-gray-500">
          Last updated: July 23, 2026
        </p>
      </div>
    ),
  },
];

export default function PrivacyPage() {
  return (
    <div className="min-h-screen bg-[#0d1117]">
      <PageMeta
        title="Privacy Policy — How Hackers Lodge Collects, Uses &amp; Protects Your Data"
        description="Learn how Hackers Lodge collects, uses, and protects your personal data. Our privacy policy covers account information, project data, communication data, cookies, data sharing, security measures, and your privacy rights."
        keywords="privacy policy, data protection, Hackers Lodge privacy, cybersecurity marketplace privacy, user data rights"
        canonicalUrl={`${siteUrl}/privacy`}
        ogType="website"
        schema={privacySchema}
      />
      <Header />
      <main className="pt-20 md:pt-24">
        <section className="py-12 md:py-16 bg-[#161b22]">
          <div className="w-full px-4 md:px-8 max-w-4xl mx-auto">
            <Breadcrumb
              items={[
                { label: "Home", to: "/" },
                { label: "Privacy Policy" },
              ]}
            />

            <div className="mb-8 text-center">
              <h1 className="text-2xl md:text-3xl font-light text-gray-200 mb-2">
                Privacy Policy
              </h1>
              <p className="text-sm text-gray-500">
                How Hackers Lodge collects, uses, and protects your data.
              </p>
            </div>

            {/* Privacy Sections */}
            <div className="bg-[#0d1117] rounded border border-gray-700 p-6 md:p-8 space-y-8">
              {sections.map((section) => (
                <article key={section.id} id={section.id} className="scroll-mt-24">
                  <h3 className="text-base font-semibold text-gray-200 mb-3">
                    {section.title}
                  </h3>
                  {section.content}
                  {section.id !== "contact" && (
                    <hr className="mt-6 border-gray-700/50" />
                  )}
                </article>
              ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}